/**
 * Platform Admin → Organizations → one organization: plan and status, usage against its plan's limits,
 * and its session policy (read-only; organization administrators set it).
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import SessionTimeoutPanel from '@/components/admin/SessionTimeoutPanel';
import ExtraDevicesPanel from '@/components/admin/ExtraDevicesPanel';
import { getTenant, updateTenant } from '@/api/tenants';
import { getTenantUsage } from '@/api/tiers';
import { useTiers } from '@/hooks/useTiers';
import type { Tenant } from '@/types/tenant';
import { apiError } from '@/utils/apiError';
import { PLAN_NAMES, PLAN_ORDER, count, effectiveMonthly } from '@/pages/platform/planFormat';

function UsageRow({ label, used, limit }: { label: string; used: number; limit: number }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900">{used.toLocaleString('en-US')} of {count(limit)}</dd>
    </div>
  );
}

export default function PlatformOrganizationPage() {
  const { id = '' } = useParams();
  const cache = useQueryClient();
  const tenant = useQuery({ queryKey: ['tenant', id], queryFn: () => getTenant(id) });
  const usage = useQuery({ queryKey: ['tenant-usage', id], queryFn: () => getTenantUsage(id) });
  const { data: plans = [] } = useTiers();
  const [form, setForm] = useState<Pick<Tenant, 'name' | 'is_active' | 'subscription_tier'> | null>(null);
  useEffect(() => { if (tenant.data) setForm({ name: tenant.data.name, is_active: tenant.data.is_active, subscription_tier: tenant.data.subscription_tier }); }, [tenant.data]);
  const save = useMutation({
    mutationFn: () => updateTenant(id, form!),
    onSuccess: () => { cache.invalidateQueries({ queryKey: ['tenants'] }); cache.invalidateQueries({ queryKey: ['tenant', id] }); cache.invalidateQueries({ queryKey: ['tenant-usage', id] }); },
  });
  if (tenant.isLoading || !form) return tenant.error ? <p role="alert" className="text-red-700">{apiError(tenant.error)}</p> : <LoadingSpinner label="Loading organization..." />;
  const plan = plans.find((p) => p.tier === usage.data?.tier_config.tier) ?? usage.data?.tier_config;

  return (
    <div className="space-y-6">
      <Link to="/platform/organizations" className="text-sm text-indigo-600 hover:underline">← Organizations</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{tenant.data!.name}</h1>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tenant.data!.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>{tenant.data!.is_active ? 'Active' : 'Inactive'}</span>
        <span className="text-sm text-gray-500">Code: {tenant.data!.slug}</span>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <form className="space-y-4 rounded-lg border border-gray-200 bg-white p-5" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <h2 className="font-semibold text-gray-900">Plan and status</h2>
          {save.error && <p role="alert" className="rounded bg-red-50 p-2 text-sm text-red-700">{apiError(save.error)}</p>}
          {save.isSuccess && <p role="status" className="rounded bg-green-50 p-2 text-sm text-green-800">Saved.</p>}
          <label className="block text-sm font-medium text-gray-700">Organization name
            <input className="input-field mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
          <label className="block text-sm font-medium text-gray-700">Plan
            <select className="input-field mt-1" value={form.subscription_tier} onChange={(e) => setForm({ ...form, subscription_tier: e.target.value as Tenant['subscription_tier'] })}>
              {PLAN_ORDER.map((tier) => <option key={tier} value={tier}>{PLAN_NAMES[tier]}</option>)}
            </select></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />Active (inactive organizations cannot sign in or scan)</label>
          <button type="submit" className="btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save changes'}</button>
        </form>

        <section aria-label="Usage" className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="font-semibold text-gray-900">Usage{plan ? ` (${PLAN_NAMES[plan.tier] ?? plan.tier} plan)` : ''}</h2>
          {usage.error && <p role="alert" className="text-sm text-red-700">{apiError(usage.error)}</p>}
          {usage.data && plan && (
            <dl className="mt-2 divide-y divide-gray-100">
              <UsageRow label="Scans this month" used={usage.data.scan_count_this_month} limit={effectiveMonthly(plan)} />
              <UsageRow label="Users" used={usage.data.user_count} limit={plan.max_users} />
              <UsageRow label="Devices" used={usage.data.device_count ?? usage.data.asset_count} limit={usage.data.device_limit ?? plan.max_assets} />
              <div className="flex justify-between gap-4 py-1.5 text-sm"><dt className="text-gray-500">Discovery ranges</dt><dd className="font-medium text-gray-900">{(usage.data.range_count ?? 0).toLocaleString('en-US')} (not counted)</dd></div>
              <UsageRow label="Credentials" used={usage.data.credential_count} limit={plan.max_credentials} />
              <UsageRow label="Agents" used={usage.data.agent_count} limit={plan.max_agents} />
            </dl>
          )}
        </section>
      </div>

      {usage.data && <ExtraDevicesPanel key={`${id}-${tenant.data!.subscription_tier}-${usage.data.extra_devices ?? 0}`} orgId={id} tier={tenant.data!.subscription_tier} extraDevices={usage.data.extra_devices ?? 0} />}

      <SessionTimeoutPanel key={`${id}-${tenant.data!.subscription_tier}`} readOnly orgId={id} />
    </div>
  );
}
